import { useMemo } from 'react';
import cn from 'classnames';
import { FormContext } from 'informed';
import { useTranslation } from 'react-i18next';

import { InformedTextField } from '@nw/design-system/omega';
import {
  convertNumberToLocaleCurrency,
  LOADING,
  SUCCESS
} from '@nw/shared/utils';

import { useNiatApplicationMachineContext } from '../../context/NiatApplicationMachineProvider';
import { SetShowCongratulationsModalEvent } from '../../types/niatApplicationMachineTypes';
import {
  GetCouponCodeDetailEvent,
  ResetErrorMessageAndApiStatusEvent
} from '../../types/productMachineTypes';
import { ApplicationProductFieldValidationSchema } from '../../utils/userRoleDetailsAndApplicationProductValidationSchema';

import { CongratulationsModal } from '../CongratulationsModal';
import { CouponCodeFieldAndApplyButtonContainer } from '../ProductFeeSection/styledComponent';

import {
  AppliedText,
  ApplyButton,
  ReferralCodeFieldContainer,
  ReferralCodeSuccessMessage
} from './styledComponents';

interface RenderCouponCodeFieldAndApplyButtonProps {
  sectionName: string;
  formProps: FormContext;
  field: string;
  labelText?: string;
  HideRightElement?: boolean;
  shouldShowDiscountMessage?: boolean;
  shouldShowClearButton?: boolean;
  shouldShowCongratulationsModal?: boolean;
  onClickApplyCoupon?: () => void;
  onClickClearCoupon?: () => void;
}
const RenderCouponCodeFieldAndApplyButton = ({
  sectionName,
  formProps,
  field,
  labelText = '',
  HideRightElement,
  shouldShowDiscountMessage = false,
  shouldShowClearButton = true,
  shouldShowCongratulationsModal = true,
  onClickApplyCoupon,
  onClickClearCoupon
}: RenderCouponCodeFieldAndApplyButtonProps): React.ReactNode => {
  const { t } = useTranslation();
  const { niatApplicationMachineState, niatApplicationMachineSend } =
    useNiatApplicationMachineContext();
  const {
    context: {
      showCongratulationsModal,
      productsSectionMachineRefDetails: {
        [sectionName]: {
          state: {
            context: {
              productDetail,
              productCode,
              errorMessages: {
                couponCodeErrorMessage,
                createPaymentOrderAndGetDetailsErrorMessage
              },
              apiStatus: { getCouponCodeDetailApiStatus }
            }
          },
          send: productMachineSend
        }
      }
    }
  } = niatApplicationMachineState;
  const isInviteCodeField = () => {
    return field === 'inviteCode';
  };
  const isGetCouponCodeDetailApiStatusSuccess = () => {
    return getCouponCodeDetailApiStatus === SUCCESS;
  };
  const { formApi, formState } = formProps;
  const { values, touched, errors } = formState;
  const fieldValue = String(values[field]);
  const formattedApplicationFeeAfterDiscount = useMemo(
    () =>
      convertNumberToLocaleCurrency(
        productDetail?.productFeeAfterDiscount ?? 0
      ),
    [productDetail?.productFeeAfterDiscount]
  );
  const setShowCongratulationsModal = (value: boolean) => {
    niatApplicationMachineSend({
      type: 'SET_SHOW_CONGRATULATIONS_MODAL',
      value
    } as SetShowCongratulationsModalEvent);
  };
  const onClickApplyOrClearCoupon = () => {
    if (isGetCouponCodeDetailApiStatusSuccess()) {
      formApi.setValue(field, '');
      onClickClearCoupon?.();
      productMachineSend({
        type: 'RESET_ERROR_MESSAGE_AND_API_STATUS',
        reset: 'STATUS',
        key: 'getCouponCodeDetailApiStatus'
      } as ResetErrorMessageAndApiStatusEvent);
      return;
    }

    if (fieldValue === '') {
      return;
    }

    onClickApplyCoupon?.();
    setShowCongratulationsModal(true);
    productMachineSend({
      type: 'GET_COUPON_CODE_DETAILS',
      inviteCode: isInviteCodeField() ? fieldValue : '',
      couponCode: isInviteCodeField() ? '' : fieldValue,
      productCode
    } as GetCouponCodeDetailEvent);
  };

  const discountAppliedAmount = convertNumberToLocaleCurrency(
    productDetail?.couponDiscount ?? 0
  );

  const renderReferralCodeSuccessMessage = () => {
    if (!isGetCouponCodeDetailApiStatusSuccess()) {
      return null;
    }
    const successMessage =
      (productDetail?.couponDiscount ?? 0) > 0
        ? `Congratulations ${discountAppliedAmount}/- discount is applied. Total Fees is now ${formattedApplicationFeeAfterDiscount}/-`
        : 'Applied';
    return (
      <>
        {shouldShowDiscountMessage ? (
          <ReferralCodeSuccessMessage>
            {successMessage}
          </ReferralCodeSuccessMessage>
        ) : null}
        {shouldShowCongratulationsModal &&
        isGetCouponCodeDetailApiStatusSuccess() &&
        showCongratulationsModal ? (
          <CongratulationsModal
            isModalOpen={showCongratulationsModal}
            onClose={() => setShowCongratulationsModal(false)}
          />
        ) : null}
      </>
    );
  };
  const renderApplyText = () => {
    if (HideRightElement) {
      return null;
    }
    return isGetCouponCodeDetailApiStatusSuccess() ? (
      <AppliedText>Applied</AppliedText>
    ) : null;
  };

  const onFieldValueChange = () => {
    if (couponCodeErrorMessage) {
      productMachineSend({
        type: 'RESET_ERROR_MESSAGE_AND_API_STATUS',
        reset: 'ERROR',
        key: 'couponCodeErrorMessage'
      } as ResetErrorMessageAndApiStatusEvent);
    }
  };
  return (
    <CouponCodeFieldAndApplyButtonContainer
      className={
        shouldShowClearButton ? '' : 'take-admission-invite-code-container'
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
    >
      <ReferralCodeFieldContainer>
        <InformedTextField
          field={field}
          touched={touched[field]}
          error={couponCodeErrorMessage}
          inputClassName={'application-fee-coupon-code-field'}
          value={values[field]}
          label={labelText ? labelText : ''}
          fieldType={'text'}
          validationSchemaObject={ApplicationProductFieldValidationSchema(t)}
          isDisabled={
            isGetCouponCodeDetailApiStatusSuccess() ||
            Boolean(createPaymentOrderAndGetDetailsErrorMessage) ||
            productDetail?.productFee <= 0
          }
          rightElement={renderApplyText}
          rightElementContainerClassName={'applied-text-container'}
          placeholder={'Enter Discount Code'}
          hintClassName={errors[field] || couponCodeErrorMessage || cn('!h-0')}
          shouldShowIcon={false}
          onValueChange={(value) => {
            onFieldValueChange();
          }}
          isMarginBottomZero={true}
        />
        {renderReferralCodeSuccessMessage()}
      </ReferralCodeFieldContainer>
      <ApplyButton
        type="button"
        variant="Primary"
        size="Small"
        className={
          !shouldShowClearButton && isGetCouponCodeDetailApiStatusSuccess()
            ? 'application-fee-apply-button invite-code-applied'
            : 'application-fee-apply-button'
        }
        onClick={onClickApplyOrClearCoupon}
        isLoading={getCouponCodeDetailApiStatus === LOADING}
        isDisabled={
          (!shouldShowClearButton && isGetCouponCodeDetailApiStatusSuccess()) ||
          errors[field] ||
          couponCodeErrorMessage ||
          fieldValue === '' ||
          createPaymentOrderAndGetDetailsErrorMessage
        }
        shouldShrinkButtonWhileLoading={false}
      >
        {isGetCouponCodeDetailApiStatusSuccess()
          ? shouldShowClearButton
            ? 'Clear'
            : 'Applied'
          : 'Apply'}
      </ApplyButton>
    </CouponCodeFieldAndApplyButtonContainer>
  );
};

export default RenderCouponCodeFieldAndApplyButton;
